import { v4 as uuidv4 } from 'uuid';

const DEFAULT_DISCUSSION_BOARD_SETTINGS = {
  allowMainResponse: true,
  allowCommentResponse: true,
  notifyMeOnResponses: false,
  allowAssigneesViewResponses: true,
  hideresponsesuntilresponded: true,
};

function richTextBlock(html) {
  return {
    _id: uuidv4(),
    element: 'richText',
    value: html || '',
  };
}

function theoryHourAwards(hours) {
  const h = Math.max(0.25, Number(hours) || 0.5);
  return [{ type: 'Theory', hours: Math.round(h * 4) / 4 }];
}

function makeNestedItem(item, { courseId, subsectionId, index, defaultPrimaryConcentration }) {
  const title = item.title;
  const type = item.type;
  const pathway = defaultPrimaryConcentration || '';

  const node = {
    id: uuidv4(),
    index,
    sort_index: index,
    color: `hsl(${Math.random() * 360}, 50%, 90%)`,
    text: title,
    title,
    type,
    primaryConcentration: pathway,
    otherConcentrations: [],
    taxonomy: (item.taxonomy || []).map((t) => ({
      ...t,
      concentration: t.concentration || pathway || null,
    })),
    hourawards: theoryHourAwards(item.estimated_theory_hours),
    status: 'Draft',
    parent: subsectionId,
    superparent: courseId || null,
    level: 'nested',
    hasChanges: true,
    reference_table: null,
    reference_id: null,
  };

  if (type === 'Content') {
    node.content = [richTextBlock(item.content_html)];
  } else if (type === 'Discussion Board') {
    node.content = [richTextBlock(item.prompt_html)];
    node.settings = { ...DEFAULT_DISCUSSION_BOARD_SETTINGS };
  } else if (type === 'Assignment') {
    node.content = '';
    node.assignments = [];
    node.ai_assignment_summary = item.summary || title;
    node.ai_needs_assignment = true;
  }

  return node;
}

function makeSubsection(lesson, ctx) {
  const { courseId, sectionId, index, defaultPrimaryConcentration } = ctx;
  const subsectionId = uuidv4();
  const nestedSections = (lesson.items || []).map((item, ni) =>
    makeNestedItem(item, {
      courseId,
      subsectionId,
      index: ni,
      defaultPrimaryConcentration,
    })
  );

  const lessonHours = nestedSections.reduce(
    (sum, n) => sum + (n.hourawards?.[0]?.hours || 0),
    0
  );

  return {
    id: subsectionId,
    index,
    sort_index: index,
    color: `hsl(${Math.random() * 360}, 60%, 85%)`,
    text: lesson.title,
    title: lesson.title,
    type: 'Lesson',
    content: '',
    nestedSections,
    expanded: true,
    primaryConcentration: defaultPrimaryConcentration || '',
    otherConcentrations: [],
    taxonomy: [],
    hourawards: lessonHours > 0 ? theoryHourAwards(lessonHours) : [],
    status: 'Draft',
    parent: sectionId,
    superparent: courseId || null,
    level: 'subsection',
    hasChanges: true,
    reference_table: null,
    reference_id: null,
  };
}

function makeModule(mod, ctx) {
  const { courseId, index, defaultPrimaryConcentration } = ctx;
  const sectionId = uuidv4();
  const subsections = (mod.lessons || []).map((lesson, li) =>
    makeSubsection(lesson, {
      courseId,
      sectionId,
      index: li,
      defaultPrimaryConcentration,
    })
  );

  const moduleHours = subsections.reduce(
    (sum, sub) => sum + (sub.hourawards?.[0]?.hours || 0),
    0
  );

  return {
    id: sectionId,
    index,
    sort_index: index,
    color: `hsl(${Math.random() * 360}, 70%, 80%)`,
    text: mod.title,
    title: mod.title,
    type: 'Module',
    content: mod.description ? [richTextBlock(`<p>${mod.description}</p>`)] : '',
    subsections,
    expanded: true,
    primaryConcentration: defaultPrimaryConcentration || '',
    otherConcentrations: [],
    taxonomy: [],
    hourawards: moduleHours > 0 ? theoryHourAwards(moduleHours) : [],
    status: 'Draft',
    parent: courseId || null,
    superparent: courseId || null,
    level: 'section',
    hasChanges: true,
    reference_table: null,
    reference_id: null,
  };
}

export function outlineToSections(outline, options = {}) {
  const { courseId = null, defaultPrimaryConcentration = '', startIndex = 0 } = options;

  return (outline.modules || []).map((mod, i) =>
    makeModule(mod, {
      courseId,
      index: startIndex + i,
      defaultPrimaryConcentration,
    })
  );
}

export function summarizeExistingSections(sections) {
  return (sections || []).slice(0, 12).map((s, i) => {
    const lessonCount = s.subsections?.length || 0;
    const itemCount = (s.subsections || []).reduce(
      (n, sub) => n + (sub.nestedSections?.length || (sub.type !== 'Lesson' ? 1 : 0)),
      0
    );
    return `Module ${i + 1}: "${s.text || s.title}" — ${lessonCount} lesson(s), ~${itemCount} item(s)`;
  });
}
